// src/components/common/InfoTip.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Tooltip } from '@mui/material';
import { HelpOutline as HelpOutlineIcon } from '@mui/icons-material';

const EXPLAIN_TOOLTIP_SX = { maxWidth: 320, fontSize: '0.8125rem', lineHeight: 1.45, fontWeight: 400 };

/**
 * A tooltip that explains what a chip, cell or label means, in text large
 * enough for a sentence or two. Opens on a tap on touch screens too.
 */
export function ExplainTooltip({ title, children, placement = 'top', ...props }) {
  return (
    <Tooltip
      title={title}
      arrow
      placement={placement}
      describeChild
      enterTouchDelay={0}
      leaveTouchDelay={8000}
      slotProps={{ tooltip: { sx: EXPLAIN_TOOLTIP_SX } }}
      {...props}
    >
      {children}
    </Tooltip>
  );
}

/**
 * A small "?" after a label that explains what the value means.
 *
 * Opens on hover and on keyboard focus, and on a tap on touch screens (where
 * there is no hover). A click does not reach the parent, so a "?" in a
 * DataGrid header explains the column without sorting it.
 */
export default function InfoTip({ title, size = 16, sx }) {
  const { t } = useTranslation(['common']);
  if (!title) return null;
  return (
    <ExplainTooltip title={title}>
      <Box
        component="span"
        role="img"
        tabIndex={0}
        aria-label={t('common:info_tip.label')}
        onClick={(e) => e.stopPropagation()}
        sx={{
          display: 'inline-flex',
          flexShrink: 0,
          color: 'text.disabled',
          cursor: 'help',
          borderRadius: '50%',
          verticalAlign: 'middle',
          '&:hover, &:focus-visible': { color: 'text.secondary' },
          '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 1 },
          ...sx,
        }}
      >
        <HelpOutlineIcon sx={{ fontSize: size }} />
      </Box>
    </ExplainTooltip>
  );
}

/** A label followed by its InfoTip, kept on one line. */
export function LabelWithInfo({ label, info, gap = 1 }) {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap, minWidth: 0, maxWidth: '100%' }}>
      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</Box>
      <InfoTip title={info} />
    </Box>
  );
}

/**
 * Give a DataGrid column a "?" in its header. Use this instead of the
 * column's `description`, which only shows on hover with no visible cue.
 *
 * In a right-aligned header (numbers) the "?" goes before the title: after
 * it, it would sit under the column's resize handle and could not be hovered.
 */
export const withInfo = (column, info) => {
  const rightAligned = (column.headerAlign ?? (column.type === 'number' ? 'right' : 'left')) === 'right';
  // Never so narrow that the grid's sort and menu icons (shown on hover) cover the "?";
  // the title ellipsizes instead
  const MIN_WIDTH = 112;
  const sizing = column.width != null
    ? { width: Math.max(column.width, MIN_WIDTH) }
    : { minWidth: Math.max(column.minWidth ?? 0, MIN_WIDTH) };
  return {
    ...column,
    ...sizing,
    renderHeader: () => (
      // The title gives way (ellipsis) before the "?" does, so the cue never slides under the header's icons
      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
        {rightAligned && <InfoTip title={info} />}
        <Box component="span" className="MuiDataGrid-columnHeaderTitle" sx={{ minWidth: 0 }}>{column.headerName}</Box>
        {!rightAligned && <InfoTip title={info} />}
      </Box>
    ),
  };
};
